import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, MessageSquare, FileText, AlertTriangle, FileCheck } from 'lucide-react';
import WorkflowTabView from '../../../components/WorkflowTabView';
import AuditorAccess from './AuditorAccess';
import RequestsInbox from './RequestsInbox';
import Evidence from './Evidence';
import ChangesMateriality from './ChangesMateriality';
import AuditorOpinion from './AuditorOpinion';
import ComingSoon from '../../ComingSoon.js';

const AuditQAWorkflow = ({ previewOnly = false }) => {
  const { t } = useTranslation();
  // Temporary flag to show Coming Soon page
  const showComingSoon = false;

  // Load data from localStorage on component mount
  const loadFromStorage = () => {
    try {
      const savedData = localStorage.getItem('auditQAWorkflowData');
      const savedStep = localStorage.getItem('auditQAWorkflowStep');
      
      if (savedData) {
        return {
          formData: JSON.parse(savedData),
          currentStep: savedStep ? parseInt(savedStep) : 1
        };
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
    
    return {
      formData: {
        auditorAccess: {},
        requestsInbox: {},
        evidence: {},
        changesMateriality: {},
        auditorOpinion: {}
      },
      currentStep: 1
    };
  };

  const { formData: initialFormData, currentStep: initialStep } = loadFromStorage();
  
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [formData, setFormData] = useState(initialFormData);

  if (showComingSoon) {
    return <ComingSoon />;
  }

  // Save data to localStorage
  const saveToStorage = (newFormData, newStep) => {
    try {
      localStorage.setItem('auditQAWorkflowData', JSON.stringify(newFormData));
      localStorage.setItem('auditQAWorkflowStep', newStep.toString());
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const handleStepClick = (stepId) => {
    setCurrentStep(stepId);
    saveToStorage(formData, stepId);
  };

  const handleSubmit = (stepData) => {
    const newFormData = {
      ...formData,
      [getStepKey(currentStep)]: stepData
    };
    setFormData(newFormData);
    saveToStorage(newFormData, currentStep);
  };

  const handleNext = () => {
    if (currentStep < 5) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      saveToStorage(formData, nextStep);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      saveToStorage(formData, prevStep);
    }
  };

  const getStepKey = (step) => {
    switch (step) {
      case 1: return 'auditorAccess';
      case 2: return 'requestsInbox';
      case 3: return 'evidence';
      case 4: return 'changesMateriality';
      case 5: return 'auditorOpinion';
      default: return 'auditorAccess';
    }
  };

  const renderCurrentStep = () => {
    const currentData = formData[getStepKey(currentStep)];

    switch (currentStep) {
      case 1:
        return (
          <AuditorAccess
            onSubmit={handleSubmit}
            onNext={handleNext}
            initialData={currentData}
          />
        );
      case 2:
        return (
          <RequestsInbox
            onSubmit={handleSubmit}
            onNext={handleNext}
            onPrevious={handlePrevious}
            initialData={currentData}
          />
        );
      case 3:
        return (
          <Evidence
            onSubmit={handleSubmit}
            onNext={handleNext}
            onPrevious={handlePrevious}
            initialData={currentData}
          />
        );
      case 4:
        return (
          <ChangesMateriality
            onSubmit={handleSubmit}
            onNext={handleNext}
            onPrevious={handlePrevious}
            initialData={currentData}
          />
        );
      case 5:
        return (
          <AuditorOpinion
            onSubmit={handleSubmit}
            onPrevious={handlePrevious}
            initialData={currentData}
          />
        );
      default:
        return <AuditorAccess />;
    }
  };

  const steps = [
    {
      id: 1,
      title: t('workflow.auditQA.steps.auditorAccess.title'),
      description: t('workflow.auditQA.steps.auditorAccess.description'),
      icon: Shield
    },
    {
      id: 2,
      title: t('workflow.auditQA.steps.requestsInbox.title'),
      description: t('workflow.auditQA.steps.requestsInbox.description'),
      icon: MessageSquare
    },
    {
      id: 3,
      title: t('workflow.auditQA.steps.evidence.title'),
      description: t('workflow.auditQA.steps.evidence.description'),
      icon: FileText
    },
    {
      id: 4,
      title: t('workflow.auditQA.steps.changesMateriality.title'),
      description: t('workflow.auditQA.steps.changesMateriality.description'),
      icon: AlertTriangle
    },
    {
      id: 5,
      title: t('workflow.auditQA.steps.auditorOpinion.title'),
      description: t('workflow.auditQA.steps.auditorOpinion.description'),
      icon: FileCheck
    }
  ];

  return (
    <WorkflowTabView
      steps={steps}
      currentStep={currentStep}
      onStepChange={handleStepClick}
      previewOnly={previewOnly}
      onComplete={() => {
        console.log('Audit Q&A workflow completed');
      }}
    >
      {renderCurrentStep()}
    </WorkflowTabView>
  );
};

export default AuditQAWorkflow;
